import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '@/components/ui/dialog';
import { 
  ArrowLeft, Users, Plus, Trash2, Mail, Phone, Shield, 
  Heart, FileText, Home, Send, Loader2, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useDemo } from '@/contexts/DemoContext';

/**
 * FamilySharingPage - Share binder access with trusted family members
 */

const PERMISSION_OPTIONS = [
  { id: 'medical', label: 'Medical Information', icon: Heart, description: 'Allergies, medications, conditions' },
  { id: 'contacts', label: 'Emergency Contacts', icon: Phone, description: 'Contact list and relationships' },
  { id: 'doctor_summary', label: 'Doctor Summary', icon: FileText, description: 'Medical summary for appointments' },
  { id: 'documents', label: 'Important Documents', icon: FileText, description: 'Document list and notes' },
  { id: 'household', label: 'Household Info', icon: Home, description: 'Home codes, WiFi, utilities' },
  { id: 'full_binder', label: 'Full Binder Access', icon: Shield, description: 'All information including financial' },
];

const FamilySharingPage = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { isDemoMode, getProfiles, getFamilySharing, updateFamilySharing } = useDemo();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [shares, setShares] = useState([]);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [newShare, setNewShare] = useState({
    name: '',
    relationship: '',
    phone: '',
    email: '',
    permissions: ['medical', 'contacts']
  });

  useEffect(() => {
    fetchData();
  }, [profileId, isDemoMode]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Demo mode: use local demo data
      if (isDemoMode) {
        const profiles = getProfiles() || [];
        const demoProfile = profiles.find(p => p.id === profileId) || profiles[0];
        const familySharing = getFamilySharing() || {};
        
        setProfile(demoProfile);
        setShares(familySharing.shares || []);
        setLoading(false);
        return;
      }
      
      const [profileRes, sharesRes] = await Promise.all([
        API.get(`/emergency-binder/profiles/${profileId}`),
        API.get(`/emergency-binder/profiles/${profileId}/shared-access`)
      ]);
      setProfile(profileRes.data);
      setShares(sharesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (!isDemoMode) {
        toast.error('Failed to load sharing settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!newShare.name || !newShare.email) {
      toast.error('Please provide a name and email');
      return;
    }

    try {
      setInviting(true);
      
      // Demo mode: add to local state
      if (isDemoMode) {
        const newShareItem = {
          id: `share_${Date.now()}`,
          ...newShare,
          invited_at: new Date().toISOString()
        };
        const updatedShares = [...shares, newShareItem];
        setShares(updatedShares);
        updateFamilySharing({ shares: updatedShares });
        setShowInviteDialog(false);
        setNewShare({
          name: '',
          relationship: '',
          phone: '',
          email: '',
          permissions: ['medical', 'contacts']
        });
        toast.success(`Invitation sent to ${newShare.name} (demo)`);
        setInviting(false);
        return;
      }
      
      const response = await API.post(`/emergency-binder/profiles/${profileId}/shared-access`, newShare);
      setShares([...shares, response.data]);
      setShowInviteDialog(false);
      setNewShare({
        name: '',
        relationship: '',
        phone: '',
        email: '',
        permissions: ['medical', 'contacts']
      });
      toast.success(`Invitation sent to ${newShare.name}`);
    } catch (error) {
      console.error('Failed to invite:', error);
      if (!isDemoMode) {
        toast.error('Failed to send invitation');
      }
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveShare = async (shareId) => {
    try {
      // Demo mode: remove from local state
      if (isDemoMode) {
        const updatedShares = shares.filter(s => s.id !== shareId);
        setShares(updatedShares);
        updateFamilySharing({ shares: updatedShares });
        toast.success('Access removed (demo)');
        return;
      }
      
      await API.delete(`/emergency-binder/profiles/${profileId}/shared-access/${shareId}`);
      setShares(shares.filter(s => s.id !== shareId));
      toast.success('Access removed');
    } catch (error) {
      console.error('Failed to remove share:', error);
      if (!isDemoMode) {
        toast.error('Failed to remove access');
      }
    }
  };

  const togglePermission = (permissionId) => {
    setNewShare(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const getPermissionLabel = (permissionId) => {
    const perm = PERMISSION_OPTIONS.find(p => p.id === permissionId);
    return perm?.label || permissionId;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="family-sharing-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/emergency-binder')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">Share With Family</h1>
              <p className="text-xs text-slate-500">{profile?.name}'s Binder</p>
            </div>
            <Button 
              onClick={() => setShowInviteDialog(true)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="invite-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Invite
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Info Card */}
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-purple-800 mb-1">Secure Family Sharing</h3>
                <p className="text-sm text-purple-700">
                  Share emergency information with trusted family members. You control 
                  exactly what information each person can access.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Shared With */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-500" />
              People With Access ({shares.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {shares.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No one has been given access yet</p>
                <p className="text-sm text-slate-400 mt-1">
                  Invite trusted family members to view emergency information
                </p>
                <Button 
                  onClick={() => setShowInviteDialog(true)} 
                  className="mt-4" 
                  variant="outline"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Invite Someone
                </Button>
              </div>
            ) : (
              shares.map((share) => (
                <div key={share.id} className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-medium text-slate-800">{share.name}</h4>
                      {share.relationship && (
                        <p className="text-sm text-slate-500">{share.relationship}</p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500"
                      onClick={() => handleRemoveShare(share.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {share.email && (
                      <span className="text-xs bg-slate-200 px-2 py-1 rounded flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {share.email}
                      </span>
                    )}
                    {share.phone && (
                      <span className="text-xs bg-slate-200 px-2 py-1 rounded flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {share.phone}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {share.permissions?.map(perm => (
                      <span key={perm} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                        {getPermissionLabel(perm)}
                      </span>
                    ))}
                  </div>
                  {share.invited_at && (
                    <p className="text-xs text-slate-400 mt-2">
                      Invited {new Date(share.invited_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </main>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Family Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={newShare.name}
                  onChange={(e) => setNewShare({...newShare, name: e.target.value})}
                  placeholder="Full name"
                  data-testid="invite-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Relationship</Label>
                <Input
                  value={newShare.relationship}
                  onChange={(e) => setNewShare({...newShare, relationship: e.target.value})}
                  placeholder="e.g., Spouse, Child"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newShare.email}
                  onChange={(e) => setNewShare({...newShare, email: e.target.value})}
                  placeholder="email@example.com"
                  data-testid="invite-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={newShare.phone}
                  onChange={(e) => setNewShare({...newShare, phone: e.target.value})}
                  placeholder="Phone number"
                />
              </div>
            </div>
            <div className="space-y-3">
              <Label>Permissions</Label>
              <div className="space-y-2">
                {PERMISSION_OPTIONS.map((perm) => {
                  const Icon = perm.icon;
                  return (
                    <div 
                      key={perm.id}
                      className="flex items-center space-x-3 p-2 rounded hover:bg-slate-50"
                    >
                      <Checkbox
                        id={perm.id}
                        checked={newShare.permissions.includes(perm.id)}
                        onCheckedChange={() => togglePermission(perm.id)}
                      />
                      <div className="flex-1">
                        <label 
                          htmlFor={perm.id} 
                          className="text-sm font-medium flex items-center gap-2 cursor-pointer"
                        >
                          <Icon className="h-4 w-4 text-slate-500" />
                          {perm.label}
                        </label>
                        <p className="text-xs text-slate-500">{perm.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleInvite}
              disabled={inviting}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="send-invite-btn"
            >
              {inviting ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" />Send Invite</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FamilySharingPage;
